import Grid from '../Generic/GridLegacyCompat';
import {
  Checkbox,
  DialogContentText,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
} from '@mui/material';
import { useState } from 'react';
import {
  ActionRequirement,
  DocumentLinkRequirement,
  MissingArrangementRequirement,
  NoteEntryRequirement,
  RequirementDefinition,
} from '../GeneratedClient';
import {
  useDirectoryModel,
  useFamilyLookup,
  usePersonLookup,
} from '../Model/DirectoryModel';
import {
  uploadFamilyFileToTenant,
  uploadV1ReferralFileToTenant,
} from '../Model/FilesModel';
import {
  useRequiredSelectedLocationContext,
  useVisibleReferrals,
} from '../Model/Data';
import { UpdateDialog } from '../Generic/UpdateDialog';
import { RequirementContext } from './RequirementContext';
import { a11yProps, TabPanel } from '../Generic/TabPanel';
import { personNameString } from '../Families/PersonName';
import { DialogHandle } from '../Hooks/useDialogHandle';
import { familyNameString } from '../Families/FamilyName';
import { add, format, formatDuration, isValid } from 'date-fns';
import { ValidateDatePicker } from '../Generic/Forms/ValidateDatePicker';
import { useV1ReferralsModel } from '../Model/V1ReferralsModel';
import { useV1ReferralNotesModel } from '../Model/V1ReferralNotesModel';
import {
  familyIdFromRequirementContext,
  getArrangementRequirementStatusLabel,
  parseRequirementValidity,
  requirementNameFromRequirement,
} from './requirementWorkflowModel';
import { useArrangementRequirementSelection } from './useArrangementRequirementSelection';
import { useRequirementCompletionForm } from './useRequirementCompletionForm';
import { useRequirementCommands } from './useRequirementCommands';
import { useRequirementExemptionForm } from './useRequirementExemptionForm';

type MissingRequirementDialogProps = {
  handle: DialogHandle;
  requirement: MissingArrangementRequirement | RequirementDefinition | string;
  context: RequirementContext;
  policy: ActionRequirement;
  v1CaseId?: string;
  canComplete: boolean;
  canExempt: boolean;
};

export function MissingRequirementDialog({
  handle,
  requirement,
  context,
  policy,
  v1CaseId,
  canComplete,
  canExempt,
}: MissingRequirementDialogProps) {
  const directory = useDirectoryModel();
  const referrals = useV1ReferralsModel();
  const referralNotes = useV1ReferralNotesModel();
  const requirementCommands = useRequirementCommands();

  const now = new Date();

  const validityDuration = parseRequirementValidity(policy.validity);

  const [tabValue, setTabValue] = useState(canComplete ? 0 : 1);
  const UPLOAD_NEW = '__uploadnew__';
  const { organizationId, locationId } = useRequiredSelectedLocationContext();

  const familyLookup = useFamilyLookup();

  const familyId = familyIdFromRequirementContext(context);

  const contextFamily = familyId ? familyLookup(familyId) : undefined;

  const rawPersonLookup = usePersonLookup();
  const personLookup = familyId
    ? rawPersonLookup.bind(null, familyId)
    : undefined;

  function requireFamilyId(): string {
    if (!familyId) throw new Error('Missing familyId for this operation.');
    return familyId;
  }

  const referralInfos = useVisibleReferrals();
  const currentReferral =
    context.kind === 'V1Referral'
      ? referralInfos.find(
          (referralInfo) =>
            referralInfo.referral.referralId === context.referralId
        )?.referral
      : undefined;

  const openV1Cases =
    contextFamily?.partneringFamilyInfo?.openV1Case !== undefined
      ? [contextFamily.partneringFamilyInfo.openV1Case]
      : [];

  const closedV1Cases =
    contextFamily?.partneringFamilyInfo?.closedV1Cases
      ?.slice()
      .sort((r1, r2) => (r1.closedAtUtc! > r2.closedAtUtc! ? -1 : 1)) || [];

  const allV1Cases = [...openV1Cases, ...closedV1Cases];
  const selectedV1Case = v1CaseId
    ? allV1Cases.find((r) => r.id === v1CaseId)
    : undefined;

  const {
    applyToArrangements,
    availableArrangements,
    selectedArrangementIds,
    toggleApplyToArrangement,
  } = useArrangementRequirementSelection({
    context,
    requirement,
    selectedV1Case,
  });
  const {
    canCompleteRequirement,
    completedAtLocal,
    documentFile,
    documentId,
    notes,
    setCompletedAtError,
    setCompletedAtLocal,
    setDocumentFile,
    setDocumentId,
    setNotes,
  } = useRequirementCompletionForm({
    canComplete,
    hasValidArrangementSelection:
      (availableArrangements.length === 0) !== applyToArrangements.length > 0,
    policy,
    uploadNewDocumentId: UPLOAD_NEW,
  });
  const {
    additionalComments,
    canExemptRequirement,
    exemptAll,
    exemptionExpiresAtLocal,
    setAdditionalComments,
    setExemptAll,
    setExemptionExpiresAtLocal,
  } = useRequirementExemptionForm({
    canExempt,
    hasValidArrangementSelection:
      (availableArrangements.length === 0) !== applyToArrangements.length > 0,
    validateExpiryDate: false,
  });

  const enableSave = () =>
    tabValue === 0
      ? // mark complete
        canCompleteRequirement
      : // grant exemption
        canExemptRequirement;

  const requirementName = requirementNameFromRequirement(requirement);

  type UploadV1ReferralFile_ReturnsDocumentId = (
    orgId: string,
    locId: string,
    referralId: string,
    file: File
  ) => Promise<string>;

  async function uploadReferralDocument(
    referralId: string,
    file: File
  ): Promise<string> {
    const returnedId = await (
      uploadV1ReferralFileToTenant as unknown as UploadV1ReferralFile_ReturnsDocumentId
    )(organizationId, locationId, referralId, file);

    await referrals.uploadReferralDocumentMetadata(
      referralId,
      returnedId,
      file.name
    );

    return returnedId;
  }

  async function markComplete() {
    let document = documentId;

    if (documentId === UPLOAD_NEW) {
      if (!documentFile) throw new Error('No document file selected.');

      if (context.kind === 'V1Referral') {
        document = await uploadReferralDocument(
          context.referralId,
          documentFile
        );
      } else {
        const fid = requireFamilyId();

        document = await uploadFamilyFileToTenant(
          organizationId,
          locationId,
          fid,
          documentFile
        );

        await directory.uploadFamilyDocument(fid, document, documentFile.name);
      }
    }

    let noteId: string | null = null;

    if (notes.trim() !== '') {
      noteId = crypto.randomUUID();

      if (context.kind === 'V1Referral' && !context.partneringFamilyId) {
        await referralNotes.createDraftReferralNote(
          context.referralId,
          noteId,
          notes,
          completedAtLocal || undefined
        );
      } else {
        const fid = requireFamilyId();

        await directory.createDraftNote(
          fid,
          noteId,
          notes,
          completedAtLocal || undefined
        );
      }
    }

    await requirementCommands.completeRequirement({
      completedAtLocal: completedAtLocal!,
      context,
      document: document === '' ? null : document,
      noteId,
      policy,
      requirementName,
      selectedArrangementIds,
    });
  }

  async function exempt() {
    await requirementCommands.exemptRequirement({
      additionalComments,
      context,
      exemptAll,
      exemptionExpiresAtLocal,
      requirement: requirement as MissingArrangementRequirement,
      requirementName,
      selectedArrangementIds,
    });
  }

  async function save() {
    if (tabValue === 0) {
      if (!canComplete) throw new Error('Missing permission to complete.');
      await markComplete();
    } else {
      if (!canExempt) throw new Error('Missing permission to exempt.');
      await exempt();
    }
  }

  return (
    <UpdateDialog
      open={handle.open}
      onClose={handle.closeDialog}
      key={handle.key}
      title={`${context.kind} Requirement: ${requirementName}`}
      enableSave={enableSave}
      onSave={save}
    >
      <Tabs
        value={tabValue}
        onChange={(_, newValue) => setTabValue(newValue)}
        indicatorColor="secondary"
        variant="fullWidth"
      >
        {canComplete && (
          <Tab value={0} label="Mark Complete" {...a11yProps(0)} />
        )}
        {canExempt && (
          <Tab value={1} label="Grant Exemption" {...a11yProps(1)} />
        )}
      </Tabs>
      {canComplete && (
        <TabPanel value={tabValue} index={0}>
          {policy.instructions && (
            <DialogContentText style={{ whiteSpace: 'pre-wrap' }}>
              {policy.instructions}
            </DialogContentText>
          )}
          {policy.infoLink && (
            <DialogContentText>
              <Link
                href={policy.infoLink}
                target="_blank"
                rel="noreferrer"
                underline="hover"
              >
                {policy.infoLink}
              </Link>
            </DialogContentText>
          )}
          <br />
          <Grid container spacing={2}>
            {requirement instanceof MissingArrangementRequirement && (
              <Grid item xs={12}>
                <FormControl component="fieldset" variant="standard">
                  <FormLabel component="legend">Complete for</FormLabel>
                  <FormGroup>
                    {availableArrangements.map((arrangement) => (
                      <FormControlLabel
                        key={arrangement.id}
                        control={
                          <Checkbox
                            size="medium"
                            checked={applyToArrangements.includes(arrangement)}
                            onChange={(_, checked) =>
                              toggleApplyToArrangement(arrangement, checked)
                            }
                            name={arrangement.id!}
                          />
                        }
                        label={
                          arrangement.arrangementType +
                          ' - ' +
                          (personLookup
                            ? personNameString(
                                personLookup(
                                  arrangement.partneringFamilyPersonId
                                )
                              )
                            : '') +
                          (context.kind === 'Family Volunteer Assignment'
                            ? ` (${familyNameString(familyLookup(context.assignment.familyId))})`
                            : '') +
                          (context.kind === 'Individual Volunteer Assignment'
                            ? ` (${personLookup ? personNameString(personLookup(context.assignment.personId)) : ''})`
                            : '') +
                          ` - ` +
                          getArrangementRequirementStatusLabel(arrangement, now)
                        }
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              {requirement instanceof MissingArrangementRequirement ? (
                <ValidateDatePicker
                  label="When was this requirement completed?"
                  value={completedAtLocal}
                  disableFuture
                  maxDate={new Date()}
                  onChange={(date) => setCompletedAtLocal(date)}
                  onErrorChange={setCompletedAtError}
                  textFieldProps={{ fullWidth: true, required: true }}
                />
              ) : (
                <ValidateDatePicker
                  label="When was this requirement completed?"
                  value={completedAtLocal}
                  disableFuture
                  onChange={(date) => setCompletedAtLocal(date)}
                  onErrorChange={setCompletedAtError}
                  textFieldProps={{ fullWidth: true, required: true }}
                />
              )}
              {validityDuration &&
                (completedAtLocal && isValid(completedAtLocal) ? (
                  <p>
                    This will be valid until{' '}
                    {format(
                      add(completedAtLocal, validityDuration),
                      'M/d/yyyy h:mm a'
                    )}
                  </p>
                ) : (
                  <p>Valid for {formatDuration(validityDuration)}</p>
                ))}
            </Grid>
            {(policy.documentLink === DocumentLinkRequirement.Allowed ||
              policy.documentLink === DocumentLinkRequirement.Required) && (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl
                    fullWidth
                    size="small"
                    required={
                      policy.documentLink === DocumentLinkRequirement.Required
                    }
                  >
                    <InputLabel id="document-label">Document</InputLabel>
                    <Select
                      labelId="document-label"
                      label="Document"
                      id="document"
                      value={documentId}
                      onChange={(e) => setDocumentId(e.target.value as string)}
                    >
                      <MenuItem key="placeholder" value="">
                        None
                      </MenuItem>
                      <MenuItem key={UPLOAD_NEW} value={UPLOAD_NEW}>
                        Upload new...
                      </MenuItem>
                      <Divider />
                      {(context.kind === 'V1Referral'
                        ? currentReferral?.uploadedDocuments
                        : contextFamily?.uploadedDocuments
                      )?.map((document) => (
                        <MenuItem
                          key={document.uploadedDocumentId}
                          value={document.uploadedDocumentId}
                        >
                          {document.uploadedFileName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  {documentId === UPLOAD_NEW && (
                    <input
                      accept="*/*"
                      multiple={false}
                      id="document-file"
                      type="file"
                      onChange={async (e) => {
                        if (e.target.files && e.target.files.length > 0) {
                          setDocumentFile(e.target.files[0]);
                        } else {
                          setDocumentFile(null);
                        }
                      }}
                    />
                  )}
                </Grid>
              </>
            )}
            {(policy.noteEntry === NoteEntryRequirement.Allowed ||
              policy.noteEntry === NoteEntryRequirement.Required) && (
              <Grid item xs={12}>
                <TextField
                  id="notes"
                  required={policy.noteEntry === NoteEntryRequirement.Required}
                  label="Notes"
                  placeholder="Space for any general notes"
                  multiline
                  fullWidth
                  variant="outlined"
                  minRows={6}
                  size="medium"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </Grid>
            )}
          </Grid>
        </TabPanel>
      )}
      {canExempt && (
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={2}>
            {requirement instanceof MissingArrangementRequirement && (
              <Grid item xs={12}>
                <FormControl component="fieldset" variant="standard">
                  <FormLabel component="legend">Exempt for</FormLabel>
                  <FormGroup>
                    {availableArrangements.map((arrangement) => (
                      <FormControlLabel
                        key={arrangement.id}
                        control={
                          <Checkbox
                            size="medium"
                            checked={applyToArrangements.includes(arrangement)}
                            onChange={(_, checked) =>
                              toggleApplyToArrangement(arrangement, checked)
                            }
                            name={arrangement.id!}
                          />
                        }
                        label={
                          `${arrangement.arrangementType} - ${
                            personLookup
                              ? personNameString(
                                  personLookup(
                                    arrangement.partneringFamilyPersonId
                                  )
                                )
                              : ''
                          } - ` +
                          getArrangementRequirementStatusLabel(arrangement, now)
                        }
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              </Grid>
            )}
            {requirement instanceof MissingArrangementRequirement &&
              (requirement.dueBy || requirement.pastDueSince) && ( // Only monitoring requirements will have one of these dates set.
                <Grid item xs={12}>
                  <Divider sx={{ marginBottom: 1 }} />
                  <FormControl component="fieldset" variant="standard">
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="medium"
                          checked={exemptAll}
                          onChange={(_, checked) => setExemptAll(checked)}
                          name="exempt-all"
                        />
                      }
                      label="Exempt ALL instances of this requirement for the selected arrangement(s)?"
                    />
                  </FormControl>
                </Grid>
              )}
            <Grid item xs={12}>
              <TextField
                id="additional-comments"
                required
                label="Additional Comments"
                placeholder="Explain why this requirement will be exempted"
                multiline
                fullWidth
                variant="outlined"
                minRows={2}
                maxRows={5}
                size="small"
                value={additionalComments}
                onChange={(e) => setAdditionalComments(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <ValidateDatePicker
                label="When does this exemption expire? (Default is never)"
                value={exemptionExpiresAtLocal}
                onChange={(date) => setExemptionExpiresAtLocal(date)}
                onErrorChange={setCompletedAtError}
                textFieldProps={{ fullWidth: true }}
              />
            </Grid>
          </Grid>
        </TabPanel>
      )}
    </UpdateDialog>
  );
}
